import React, { useCallback, useEffect, useState } from 'react';
import { ErrorMessage } from '@hookform/error-message';
import { Controller, useForm } from 'react-hook-form';
import { format, startOfMonth } from 'date-fns';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import Tour from 'reactour';
import {
  Button,
  Col,
  FormGroup,
  Input,
  Modal,
  ModalBody,
  ModalHeader,
  Row
} from 'reactstrap';

import Datepicker from '../../../../../../components/Inputs/DatePicker';
import HourMaskCenter from '../../../../../../components/Inputs/HourMaskCenter';
import ReactLoader from '../../../../../../components/Loader';
import { buttonStyle } from '../../../../../../components/Containers/ConfigurationContainer/components/utils';
import { matrixSLAHolidaysTourSteps } from '../../../../utils/matrixSLATourSteps';

import {
  hourRules,
  dateRules,
  DEFAULT_END_HOUR,
  DEFAULT_START_HOUR,
  DATE_FORMAT,
  convertToInternationalDateFormat,
  convertToLongTimeFormat
} from '../../../../utils';

import MatricesSLAService from '../services';

const MatrixSLAHolidaysModal = ({
  modal,
  toggle,
  matrixSLAId,
  fetchMatrixSLAHolidays,
  currentHoliday,
  setCurrentHoliday
}) => {
  const systemConfiguration = useSelector((state) => state.systemConfiguration);
  const { handleSubmit, control, setValue, errors, reset } = useForm();
  const [loading, setLoading] = useState(false);

  const [matrixSLATourOpen, setMatrixSLATourOpen] = useState(false);
  const [descriptionLength, setDescriptionLength] = useState(0);

  const errorFormMessage = useCallback(
    (message) => <p style={{ color: 'red' }}>{message}</p>,
    []
  );

  useEffect(() => {
    if (!modal) {
      setCurrentHoliday(null);
    }
  }, [modal, setCurrentHoliday]);

  const getDateBegin = useCallback(() => {
    const date = new Date();
    const firstDayMonth = new Date(
      date.getFullYear('pt-br'),
      date.getMonth('pt-br'),
      1
    );
    return format(startOfMonth(firstDayMonth), DATE_FORMAT);
  }, []);

  useEffect(() => {
    if (currentHoliday) {
      const [year, month, day] = currentHoliday.date.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      const start_hour = currentHoliday.start_hour.slice(0, 5);
      const end_hour = currentHoliday.end_hour.slice(0, 5);
      reset({
        name: currentHoliday.name,
        description: currentHoliday.description,
        date: format(date, DATE_FORMAT),
        start_hour: start_hour,
        end_hour: end_hour
      });
    } else {
      reset({
        name: '',
        description: '',
        date: getDateBegin(),
        start_hour: DEFAULT_START_HOUR.slice(0, 5),
        end_hour: DEFAULT_END_HOUR.slice(0, 5)
      });
    }
  }, [currentHoliday, reset, setValue, getDateBegin]);

  const onSubmit = (data) => {
    const formattedData = {
      ...data,
      date: convertToInternationalDateFormat(data.date),
      start_hour: convertToLongTimeFormat(data.start_hour),
      end_hour: convertToLongTimeFormat(data.end_hour),
      sla_matrix_id: parseInt(matrixSLAId)
    };

    setLoading(true);
    const usrLogin = localStorage.getItem('login');
    if (currentHoliday) {
      MatricesSLAService.updateMatrixSLAHolidays(
        currentHoliday.id,
        formattedData,
        usrLogin
      )
        .then((response) => {
          toast.success(
            response.data.message || 'Feriado atualizado com sucesso!'
          );
          fetchMatrixSLAHolidays();
          setLoading(false);
          reset();
          toggle();
        })
        .catch((err) => {
          console.error(err);
          toast.error(
            err.response?.data?.message ||
              err.message ||
              'Erro ao criar feriado!'
          );
          setLoading(false);
        });
      return;
    }

    MatricesSLAService.createMatrixSLAHolidays(formattedData, usrLogin)
      .then((response) => {
        fetchMatrixSLAHolidays();
        setLoading(false);
        reset();
        toggle();
        toast.success(response.data.message || 'Feriado criado com sucesso!');
      })
      .catch((err) => {
        console.error(err);
        toast.error(
          err.response?.data?.message || err.message || 'Erro ao criar feriado!'
        );
        setLoading(false);
      });
  };

  const openMatrixSLATour = () => setMatrixSLATourOpen(true);

  const closeMatrixSLATour = () => setMatrixSLATourOpen(false);

  return (
    <Modal isOpen={modal} toggle={toggle} centered={true}>
      <ModalHeader toggle={toggle}>Feriados da Matriz de SLA</ModalHeader>
      <ModalBody>
        {loading ? (
          <Row className="justify-content-md-center">
            <ReactLoader />
          </Row>
        ) : (
          <FormGroup>
            <Row>
              <Col sm="12" className="mb-3 tour1-dt_matrix_sla_holidays">
                <label className="form-control-label text-capitalize">
                  Nome do Feriado*
                </label>
                <Controller
                  control={control}
                  name="name"
                  defaultValue=""
                  render={(props) => <Input type="text" {...props} />}
                  rules={{
                    required: 'O nome do feriado é obrigatório!'
                  }}
                />
                <ErrorMessage
                  errors={errors}
                  name="name"
                  render={({ message }) => errorFormMessage(message)}
                />
              </Col>
            </Row>
            <Row>
              <Col sm="12" className="mb-3 tour2-dt_matrix_sla_holidays">
                <label className="form-control-label text-capitalize">
                  Descrição
                </label>
                <Controller
                  control={control}
                  name="description"
                  defaultValue=""
                  render={(props) => {
                    const handleInputChange = (e) => {
                      setDescriptionLength(e.target.value.length);
                      props.onChange(e);
                    };

                    return (
                      <>
                        <Input
                          type="textarea"
                          rows="3"
                          name="description"
                          value={props.value}
                          onChange={handleInputChange}
                          placeholder="Descrição"
                          maxLength="255"
                        />
                        <div
                          style={{
                            color: descriptionLength === 255 ? 'red' : 'black'
                          }}
                        >
                          {descriptionLength}/255 caracteres
                        </div>
                      </>
                    );
                  }}
                  rules={{
                    required: 'Campo obrigatório',
                    maxLength: {
                      value: 255,
                      message: 'A descrição não pode ter mais de 255 caracteres'
                    }
                  }}
                />
                <ErrorMessage
                  errors={errors}
                  name="description"
                  render={({ message }) => errorFormMessage(message)}
                />
              </Col>
            </Row>
            <Row>
              <Col sm="12" className="mb-3 tour3-dt_matrix_sla_holidays">
                <label className="form-control-label">Data inicial</label>
                <Controller
                  render={(props) => (
                    <Datepicker
                      fieldName="date"
                      valueController={setValue}
                      defaultValue={props.value}
                    />
                  )}
                  control={control}
                  name="date"
                  rules={dateRules}
                  defaultValue={getDateBegin()}
                />
              </Col>
            </Row>
            <Row className="mt-3">
              <Col sm="6" className="mb-3 tour4-dt_matrix_sla_holidays">
                <label className="form-control-label text-capitalize">
                  Hora de Inicio*
                </label>
                <Controller
                  render={(props) => (
                    <HourMaskCenter
                      valueController={setValue}
                      fieldName="start_hour"
                      value={props.value}
                      placeholder=""
                    />
                  )}
                  control={control}
                  name="start_hour"
                  rules={hourRules}
                  defaultValue="08:00"
                />
                <ErrorMessage
                  errors={errors}
                  name="start_hour"
                  render={({ message }) => errorFormMessage(message)}
                />
              </Col>
              <Col sm="6" className="mb-3 tour5-dt_matrix_sla_holidays">
                <label className="form-control-label text-capitalize">
                  Hora de Fim*
                </label>
                <Controller
                  render={(props) => (
                    <HourMaskCenter
                      valueController={setValue}
                      fieldName="end_hour"
                      value={props.value}
                      placeholder=""
                    />
                  )}
                  control={control}
                  name="end_hour"
                  rules={hourRules}
                  defaultValue="18:00"
                />
                <ErrorMessage
                  errors={errors}
                  name="end_hour"
                  render={({ message }) => errorFormMessage(message)}
                />
              </Col>
            </Row>
            <Row
              className="mt-3 ml-1"
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                width: '100%'
              }}
            >
              <div>
                <Button
                  style={buttonStyle(systemConfiguration.primary_color)}
                  onClick={() => handleSubmit(onSubmit)()}
                >
                  Salvar
                </Button>

                <Button
                  color="danger"
                  onClick={() => {
                    reset();
                    toggle();
                  }}
                >
                  Cancelar
                </Button>
              </div>

              <>
                <Button
                  className="float-right "
                  type="button"
                  outline
                  color="info"
                  onClick={openMatrixSLATour}
                  title="Saiba mais sobre as configurações dos Feriados"
                >
                  Saiba Mais
                </Button>
                <Tour
                  steps={matrixSLAHolidaysTourSteps}
                  isOpen={matrixSLATourOpen}
                  onRequestClose={closeMatrixSLATour}
                />
              </>
            </Row>
          </FormGroup>
        )}
      </ModalBody>
    </Modal>
  );
};

export default MatrixSLAHolidaysModal;
